(() => {
  if (customElements.get('inewer-event-date')) return;
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const today = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); };
  const iso = d => [d.getFullYear(),String(d.getMonth()+1).padStart(2,'0'),String(d.getDate()).padStart(2,'0')].join('-');
  const parse = value => { const p = value.split('-').map(Number); return new Date(p[0],p[1]-1,p[2]); };
  const english = d => months[d.getMonth()].slice(0,3)+' '+d.getDate()+', '+d.getFullYear();
  const eventLabel = value => { const p = value.split('-').map(Number); return months[p[1]-1]+' '+p[2]; };
  const dateKey = value => Number(value.replaceAll('-',''));
  const arrivalWindow = range => {
    const matches = Array.from((range?.textContent || '').matchAll(/([A-Za-z]{3})\s+(\d{1,2})/g));
    if (matches.length < 2) return null;
    const current = today();
    const part = match => {
      const month = months.findIndex(name => name.slice(0,3).toLowerCase() === match[1].toLowerCase());
      if (month < 0) return null;
      const day = Number(match[2]);
      const nextYear = month < current.getMonth() || (month === current.getMonth() && day < current.getDate());
      const year = current.getFullYear() + Number(nextYear);
      return {key:year*10000+(month+1)*100+day,label:match[1]+' '+day,value:iso(new Date(year,month,day))};
    };
    const start = part(matches[0]), end = part(matches.at(-1));
    return start && end ? {start,end} : null;
  };
  class EventDate extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const on = (target,type,fn) => target.addEventListener(type,fn,{signal:this.controller.signal});
      this.trigger = this.querySelector('.ied-trigger');
      this.input = this.querySelector('[data-inewer-event-date]');
      this.panel = this.querySelector('.ied-calendar');
      this.help = this.querySelector('.ied-help');
      this.defaultHelp = this.help?.textContent || '';
      this.delivery = this.closest('product-info')?.querySelector('.inewer-delivery-estimate');
      if (this.help) this.help.setAttribute('aria-live','polite');
      if (this.delivery) {
        this.delivery.querySelector('.inewer-delivery-estimate__badge')?.remove();
        const footnote = this.delivery.querySelector('.inewer-delivery-estimate__footnote');
        if (footnote) footnote.textContent = 'Estimated dates include processing and shipping time. Shipping options are selected at checkout.';
        const options = this.delivery.querySelectorAll('.inewer-delivery-estimate__option');
        const standardWindow = arrivalWindow(options[0]?.querySelector('.inewer-delivery-estimate__range'));
        const rushWindow = arrivalWindow(options[1]?.querySelector('.inewer-delivery-estimate__range'));
        this.rushOption = options[1];
        this.standardStart = standardWindow?.start.key;
        this.standardEnd = standardWindow?.end.key;
        this.standardEndLabel = standardWindow?.end.label;
        this.rushStart = rushWindow?.start.key;
        this.rushEnd = rushWindow?.end.key;
        this.minimumDate = rushWindow?.start.value;
        this.minimumLabel = rushWindow?.start.label;
      }
      if (!this.minimumDate) this.minimumDate = iso(today());
      if (!this.minimumLabel) this.minimumLabel = eventLabel(this.minimumDate);
      this.defaultHelp = 'Earliest available event date: '+this.minimumLabel+'.';
      if (this.help) this.help.textContent = this.defaultHelp;
      const error = this.querySelector('.ied-error');
      if (error) error.textContent = 'This event date is too soon for delivery. Please select '+this.minimumLabel+' or later.';
      this.trigger.disabled = false;
      on(this.trigger,'click',() => this.panel.hidden ? this.open() : this.close());
      on(this.input,'change',() => this.updateDeliveryStatus());
      on(this.panel,'click',event => {
        const b = event.target.closest('button');
        if (!b || b.disabled) return;
        if (b.dataset.day) this.choose(b.dataset.day);
        else if (b.dataset.move) {
          this.view = new Date(this.view.getFullYear(),this.view.getMonth()+Number(b.dataset.move),1);
          this.render();
        } else if (b.dataset.action === 'clear') this.choose('');
        else if (b.dataset.action === 'today') this.choose(iso(today()));
        else if (b.dataset.action === 'close') this.close(true);
      });
      on(this.panel,'change',() => {
        this.view = new Date(Number(this.panel.querySelector('[data-year]').value),Number(this.panel.querySelector('[data-month]').value),1);
        const minimum = parse(this.minimumDate);
        if (this.view < new Date(minimum.getFullYear(),minimum.getMonth(),1)) this.view = new Date(minimum.getFullYear(),minimum.getMonth(),1);
        this.render();
      });
      on(this,'keydown',event => {
        if (event.key === 'Escape') { event.preventDefault(); this.close(true); return; }
        const day = event.target.dataset.day;
        if (!day) return;
        let d = parse(day);
        const shifts = {ArrowLeft:-1,ArrowRight:1,ArrowUp:-7,ArrowDown:7};
        if (event.key in shifts) d.setDate(d.getDate()+shifts[event.key]);
        else if (event.key === 'Home') d.setDate(d.getDate()-d.getDay());
        else if (event.key === 'End') d.setDate(d.getDate()+6-d.getDay());
        else if (event.key === 'PageUp' || event.key === 'PageDown') {
          const step = event.key === 'PageUp' ? -1 : 1;
          const last = new Date(d.getFullYear(),d.getMonth()+step+1,0).getDate();
          d = new Date(d.getFullYear(),d.getMonth()+step,Math.min(d.getDate(),last));
        } else return;
        event.preventDefault();
        const minimum = parse(this.minimumDate);
        if (d < minimum) d = minimum;
        this.view = new Date(d.getFullYear(),d.getMonth(),1);
        this.render(iso(d));
      });
      on(document,'click',event => { if (!event.composedPath().includes(this)) this.close(); });
      on(this,'focusout',event => { if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close(); });
      this.updateDeliveryStatus();
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    open() {
      const minimum = parse(this.minimumDate);
      const d = this.input.value && parse(this.input.value) >= minimum ? parse(this.input.value) : minimum;
      this.view = new Date(d.getFullYear(),d.getMonth(),1);
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded','true');
      this.render(iso(d));
    }
    close(focus = false) {
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded','false');
      if (focus) this.trigger.focus();
    }
    choose(value) {
      if (value && value < this.minimumDate) return;
      this.input.value = value;
      this.input.disabled = !value;
      this.querySelector('[data-date-label]').textContent = value ? english(parse(value)) : 'Select date';
      this.querySelector('.ied-error').hidden = true;
      this.input.dispatchEvent(new Event('change',{bubbles:true}));
      this.close(true);
    }
    updateDeliveryStatus() {
      if (!this.help) return;
      if (!this.input.value || !this.standardStart || !this.standardEnd || !this.rushStart || !this.rushEnd) {
        this.rushOption?.classList.remove('ied-rush-safer');
        this.help.textContent = this.defaultHelp;
        return;
      }
      const event = dateKey(this.input.value);
      const rushIsRecommended = event < this.standardStart && event >= this.rushEnd;
      this.rushOption?.classList.toggle('ied-rush-safer',rushIsRecommended);
      if (event >= this.standardEnd) {
        this.help.textContent = 'Estimated to arrive by your '+eventLabel(this.input.value)+' event.';
      } else if (event >= this.standardStart) {
        this.help.textContent = 'Standard may arrive as late as '+this.standardEndLabel+'. Rush shipping is the safer option for your event date.';
      } else if (event >= this.rushEnd) {
        this.help.textContent = 'Standard is estimated to arrive after your event date. Rush shipping is recommended for arrival by your event date.';
      } else if (event >= this.rushStart) {
        this.help.textContent = 'Rush shipping is required and is expected to arrive by your event date.';
      } else {
        this.help.textContent = 'Our earliest estimated arrival is after your event date.';
      }
    }
    render(focusDay) {
      const current = today(), minimum = parse(this.minimumDate), y = this.view.getFullYear(), m = this.view.getMonth();
      const active = document.activeElement;
      const restore = this.panel.contains(active) ? active.dataset.move ? '[data-move="'+active.dataset.move+'"]' : active.hasAttribute('data-year') ? '[data-year]' : active.hasAttribute('data-month') ? '[data-month]' : null : null;
      const firstYear = Math.min(minimum.getFullYear(),y);
      let years = '';
      for (let year = firstYear; year <= Math.max(current.getFullYear()+10,y); year++) years += '<option'+(year===y?' selected':'')+'>'+year+'</option>';
      const monthOptions = months.map((name,i) => '<option value="'+i+'"'+(i===m?' selected':'')+(y===minimum.getFullYear() && i<minimum.getMonth()?' disabled':'')+'>'+name+'</option>').join('');
      let days = '<span></span>'.repeat(new Date(y,m,1).getDay());
      const selected = this.input.value;
      const focus = focusDay || (selected && parse(selected).getMonth()===m && parse(selected).getFullYear()===y ? selected : iso(new Date(y,m,y===minimum.getFullYear() && m===minimum.getMonth()?minimum.getDate():1)));
      for (let n=1;n<=new Date(y,m+1,0).getDate();n++) {
        const d = new Date(y,m,n), value = iso(d);
        days += '<button type="button" data-day="'+value+'" aria-label="'+months[m]+' '+n+', '+y+'" aria-pressed="'+(value===selected)+'" tabindex="'+(value===focus?0:-1)+'"'+(d<minimum?' disabled':'')+(value===iso(current)?' aria-current="date"':'')+'>'+n+'</button>';
      }
      this.panel.innerHTML = '<div class="ied-nav"><button type="button" data-move="-1" aria-label="Previous month"'+(new Date(y,m,1)<=new Date(minimum.getFullYear(),minimum.getMonth(),1)?' disabled':'')+'>&lsaquo;</button><select data-month aria-label="Month">'+monthOptions+'</select><select data-year aria-label="Year">'+years+'</select><button type="button" data-move="1" aria-label="Next month">&rsaquo;</button></div><div class="ied-week" aria-hidden="true">'+['Su','Mo','Tu','We','Th','Fr','Sa'].map(s=>'<span>'+s+'</span>').join('')+'</div><div class="ied-days">'+days+'</div><div class="ied-footer"><button type="button" data-action="clear">Clear</button><button type="button" data-action="today"'+(today()<minimum?' disabled':'')+'>Today</button><button type="button" data-action="close">Close</button></div>';
      if (focusDay) this.panel.querySelector('[data-day="'+focusDay+'"]')?.focus();
      else if (restore) this.panel.querySelector(restore)?.focus();
      // Keep the popup attached to the full-width trigger without depending on font size.
      this.panel.style.top = (this.trigger.offsetTop + this.trigger.offsetHeight + 4)+'px';
    }
    validate() {
      const valid = !this.input.value || this.input.value >= this.minimumDate;
      this.querySelector('.ied-error').hidden = valid;
      if (!valid) this.trigger.focus();
      return valid;
    }
  }
  customElements.define('inewer-event-date',EventDate);
  const validate = (form,event) => {
    for (const input of Array.from(form.elements)) {
      if (input.matches('[data-inewer-event-date]') && !input.closest('inewer-event-date').validate()) {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
    }
  };
  document.addEventListener('submit',event => { if (event.target instanceof HTMLFormElement) validate(event.target,event); },true);
  document.addEventListener('click',event => {
    const form = event.target.closest('.shopify-payment-button')?.closest('form');
    if (form) validate(form,event);
  },true);
})();
